
import { useEffect, useState } from 'react';
import { UserPlus, Search, Trophy, Lock, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext'; 
import api from '../../lib/api.ts'; 

// Fallback icon logic based on title keywords or step position
const getFallbackIcon = (title ,index) => {
  const lowerTitle = title.toLowerCase();
  
  if (lowerTitle.includes('register') || lowerTitle.includes('account') || index === 0) {
    return UserPlus;
  }
  if (lowerTitle.includes('browse') || lowerTitle.includes('search') || lowerTitle.includes('explore') || index === 1) {
    return Search;
  }
  if (lowerTitle.includes('win') || lowerTitle.includes('auction') || lowerTitle.includes('bid') || index === 2) {
    return Trophy;
  }
  if (lowerTitle.includes('secure') || lowerTitle.includes('pay') || lowerTitle.includes('payment') || lowerTitle.includes('complete') || index === 3) {
    return Lock;
  }
  
  // Default fallback for any extra steps
  return CheckCircle;
};

export function BuyerJourney({ header: propHeader, steps: propSteps }) {
  const { translate, currentLanguage } = useLanguage(); // Assuming context provides currentLanguage ('en' / 'ar')

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!propHeader || !propSteps);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (propHeader && propSteps) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch buyer page settings (adjust query param if your backend expects different format)
        const response = await api.get('/pagesettings/clientbuyerpage', {
          params: { lang: currentLanguage }, // or remove if backend auto-detects from headers
        });

        setData(response.data);
      } catch (err) {
        console.error('Failed to load buyer journey:', err);
        setError('Unable to load buyer journey information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentLanguage, propHeader, propSteps]); // Re-fetch when language changes

  if (loading) {
    return (
      <section className="bg-bg-black py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">
          Loading buyer journey...
        </div>
      </section>
    );
  }

  const header = propHeader || data?.buyer_journey_header?.[0];
  const steps = propSteps || data?.buyer_journey;

  if (error || !header || !steps?.length) {
    // Fallback to translated static content if API fails or data is missing
    return null;
  }

  return (
    <section className="bg-bg-black py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Dynamic Header */}
        <div className="text-center mb-16 sm:mb-24">
          <h2 className="text-3xl sm:text-4xl lg:text-[40px] font-black text-white mb-6 uppercase tracking-tight">
            {header.title.value || translate('newHome.buyerJourney.title')}
          </h2>
          <p
            className="text-gray-500 text-base sm:text-lg max-w-3xl mx-auto font-medium leading-relaxed"
            dangerouslySetInnerHTML={{ __html: header.description.value || translate('newHome.buyerJourney.subtitle') }}
          />
        </div>

        {/* Dynamic Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 sm:gap-16">
          {steps.map((step, idx) => {
            const Icon = getFallbackIcon(step.title.value, idx);
            const hasCustomIcon = step.icon?.value && step.icon.value.trim() !== '';

            return (
              <div key={idx} className="flex flex-col items-center text-center group">
                <div className="w-20 h-20 bg-[#0a0c10] border border-blue-500/20 rounded-[20px] flex items-center justify-center mb-8 relative transition-all group-hover:border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.05)] overflow-hidden">
                  {hasCustomIcon ? (
                    <img
                      src={step.icon.value}
                      alt={step.title.value}
                      className="w-10 h-10 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'; // Hide broken image
                        e.currentTarget.nextSibling?.style.removeProperty('display'); // Show fallback
                      }}
                    />
                  ) : null}

                  <Icon
                    className="w-8 h-8 text-[#3b82f6]"
                    strokeWidth={1.5}
                    style={{ display: hasCustomIcon ? 'none' : 'block' }} // Hide if image loaded
                  />
                </div>

                <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
                  <span className="text-white">{idx + 1}.</span>
                  <span>{step.title.value}</span>
                </h3>

                <p className="text-gray-500 text-sm leading-relaxed font-medium px-4">
                  {step.description.value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}